/* eslint-disable parog-web/no-glint-expect-error-directive */
import type Store from '@ember-data/store';
import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { BufferedChangeset } from 'ember-changeset/types';
import t from 'ember-intl/helpers/t';
import type { ForcBaseArgs } from 'parog-web/components/forc/abstract/base-component';
import ForcBaseComponent from 'parog-web/components/forc/abstract/base-component';
import Provider from 'parog-web/components/forc/provider';
import Details from 'parog-web/components/prescription/medication/details';
import TreatmentFormModal from 'parog-web/components/treatment/treatment-form-modal';
import MedicationPrescribedJournalEvent from 'parog-web/models/medication-prescribed-journal-event';
import type Patient from 'parog-web/models/patient';
import type MedicationRequest from 'parog-web/models/prescriptions/medication-request';
import type AnalyticsService from 'parog-web/services/analytics';
import Checkbox from 'ui-kit/components/checkbox';
import LabeledTextArea from 'ui-kit/components/labeled-text-area';
import LinkButton from 'ui-kit/components/link-button';
import UiCheckboxWithBlock from 'ui-kit/components/ui-checkbox-with-block';
import fuiDropdown from 'wise-fui-modules/modifiers/fui-dropdown';

interface Period {
  duration: number;
  unit: 'day' | 'days' | 'week' | 'weeks' | 'month' | 'months' | 'year';
}

interface Args extends ForcBaseArgs {
  changeset: BufferedChangeset;
  patient: Patient;
  oldMedicationPrescribed: MedicationPrescribedJournalEvent | undefined;
}

export default class PrescriptionMedicationEditionForm extends ForcBaseComponent<Args> {
  static fullComponentId = 'prescription/medication-edition-form';
  static journalEventClass = MedicationPrescribedJournalEvent;

  @service declare analytics: AnalyticsService;
  @service declare router: RouterService;
  @service declare store: Store;

  @tracked declare chatMessage: string;
  @tracked declare willSendMessage: boolean;

  get changeset() {
    return this.args.changeset;
  }

  get hasPps(): boolean {
    return !!this.latestTreatmentPrescription?.prescriptions?.length;
  }

  get isWholeCarePlanSelected() {
    return (
      this.changeset.get('medications').length ===
      this.latestTreatmentPrescription?.prescriptions.length
    );
  }

  get latestTreatmentPrescription() {
    return this.patient.lastTreatmentPrescriptionChangedJournalEvent;
  }

  get suggestionComments() {
    return [
      'Vaccination anti-grippale',
      'Vaccination anti-pneumocoque (PREVENAR 13, PNEUVOVAX)',
      'Rappel de vaccination anti-pneumocoque (PNEUMOVAX)',
      'Vaccination anti covid'
    ];
  }

  get validityPeriod() {
    const periodArray: Period[] = [];

    periodArray.push({ duration: 1, unit: 'day' });
    for (let i = 2; i <= 13; i++) {
      periodArray.push({
        duration: i,
        unit: 'days'
      });
    }
    periodArray.push(
      { duration: 1, unit: 'week' },
      { duration: 2, unit: 'weeks' },
      { duration: 3, unit: 'weeks' }
    );
    periodArray.push({ duration: 1, unit: 'month' });
    for (let i = 2; i <= 6; i++) {
      periodArray.push({
        duration: i,
        unit: 'months'
      });
    }
    periodArray.push({ duration: 1, unit: 'year' });
    return periodArray;
  }

  constructor(owner: Owner, args: Args) {
    super(owner, args);

    this.chatMessage =
      'Bonjour,\nJe viens de vous éditer une nouvelle ordonnance. Elle est jointe à ce message.\nBonne journée.';
    this.willSendMessage = true;
  }

  override afterCancel: () => void = () => {
    this.router.transitionTo(
      'medics-web.protected.patients.patient.dashboard',
      this.args.patient.id
    );
  };

  appendVaccine = (changeset, str) => {
    const prevVaccins = changeset.get('otherMedicationsAld') || '';
    switch (str) {
      case 'Vaccination anti-grippale':
        changeset.set(
          'otherMedicationsAld',
          `${prevVaccins}` +
            '• VACCINATION ANTI-GRIPPALE\n    1 injection IM dès que le vaccin est disponible\n'
        );
        break;
      case 'Vaccination anti-pneumocoque (PREVENAR 13, PNEUVOVAX)':
        changeset.set(
          'otherMedicationsAld',
          `${prevVaccins}` +
            '• VACCINATION ANTI-PNEUMOCOQUE\n    PREVENAR 13 : 1 injection IM puis 8 semaines plus tard\n    PNEUMOVAX : 1 injection IM \n'
        );
        break;
      case 'Rappel de vaccination anti-pneumocoque (PNEUMOVAX)':
        changeset.set(
          'otherMedicationsAld',
          `${prevVaccins}` +
            '• RAPPEL DE VACCINATION ANTI-PNEUMOCOQUE\n    PNEUMOVAX : 1 injection IM \n'
        );
        break;
      case 'Vaccination anti covid':
        changeset.set(
          'otherMedicationsAld',
          `${prevVaccins}` + '• Vaccination anti-COVID en très haute priorité\n'
        );
        break;
    }
  };

  hasMedication = (medications: unknown, medication: MedicationRequest) =>
    (medications as MedicationRequest[]).some(m => m.isEqual(medication));

  async processRenewAlertTriggered() {
    const alertsTriggered = this.store.peekAll('alert-triggered-journal-event');
    const alert = alertsTriggered.find(
      alert =>
        alert.subType == 'medication-prescription' &&
        alert.triggeringJournalEvent instanceof
          MedicationPrescribedJournalEvent &&
        alert.triggeringJournalEvent.id == this.args.oldMedicationPrescribed?.id
    );
    if (alert?.isUnprocessed) {
      await alert.processAlert({
        actionTaken: 'medication-prescription-renewed'
      });
    }
  }

  override async save() {
    this.shouldForceQuit = true;
    await this.args.changeset.validate();

    if (this.args.changeset.get('isValid')) {
      await this.args.changeset.save();
      const savedModel = this.args.changeset
        .data as MedicationPrescribedJournalEvent;

      if (
        this.patient.authorizesAppUsage &&
        this.willSendMessage &&
        this.chatMessage.trim()
      ) {
        await this.sendChatMessages();
      }

      await this.processRenewAlertTriggered();

      this.router.transitionTo(
        'medics-web.protected.patients.patient.prescriptions.medication-display',
        savedModel.patient.id,
        savedModel.id
      );
    }
  }

  async sendChatMessages() {
    const chatMessageJe = this.store.createRecord(
      'patient-chat-message-sent-journal-event',
      {
        occurredAt: new Date(),
        patient: this.patient,
        body: this.chatMessage
      }
    );
    this.analytics.trackJeCreation(chatMessageJe);
    await chatMessageJe.save();

    await this.store
      .createRecord('patient-chat-message-sent-journal-event', {
        occurredAt: new Date(),
        triggeringJournalEvent: this.args.changeset.data,
        patient: this.patient,
        body: 'Nouvelle ordonnance reçue'
      })
      .save();
  }

  /**
   * Roll backs changeset
   * The changeset is rolled back in case the Medic already start completed
   * the form. This ensure no "Are you sure you want to leave the form" alert is
   * shown, and fixes a "too many recursion error" if this alert is displayed.
   */
  showTreatmentForm = () => {
    this.changeset.rollback();
  };

  toggleCarePlanSelection = checked => {
    if (checked) {
      this.changeset.set(
        'medications',
        this.latestTreatmentPrescription?.prescriptions.slice()
      );
    } else {
      this.changeset.set('medications', []);
    }
  };

  togglePrescription = (prescription: MedicationRequest) => {
    let selection: MedicationRequest[] = this.changeset
      .get('medications')
      .slice();

    const finded = selection.find(m => m.isEqual(prescription));
    if (finded) {
      selection = selection.filter(m => !m.isEqual(prescription));
    } else {
      selection.push(prescription);
    }

    this.changeset.set('medications', selection);
  };

  unloadMedicationPrescribed = () => {
    this.args.patient.medicationPrescribedJournalEvents.forEach(je => {
      if (!je.id) je.unloadRecord();
    });
  };

  updateChatMessage = event => {
    this.chatMessage = event.target.value;
  };

  override willDestroy(): void {
    super.willDestroy();
    this.unloadMedicationPrescribed();
  }

  private setWillSendMessage = (value: boolean): void => {
    this.willSendMessage = value;
  };

  <template>
    {{! @glint-expect-error }}
    <Provider @forcComponent={{this}} @title='Ordonnance médicamenteuse'>
      <:content as |Form|>
        <div class='field'>
          <Form.Dropdown
            @labelName='Durée de validité'
            @placeholder='Durée de validité'
            @propertyName='validityPeriod'
            @isMandatory={{false}}
            as |dropdown|
          >
            {{#each this.validityPeriod as |period|}}
              <dropdown.Item
                @key={{concat period.duration '-' period.unit}}
                @value={{concat
                  period.duration
                  ' '
                  (t (concat 'prescriptions.validity-period.' period.unit))
                }}
              />
            {{/each}}
          </Form.Dropdown>
        </div>
        <div class='field'>
          <Form.Checkbox
            @labelName='Je souhaite être alerté lorsque cette ordonnance arrivera à expiration'
            @payloadKey='createExpirationAlert'
          />
        </div>
        <div class='field'>
          <label>Médicament ALD du plan de soins</label>
          {{#if this.hasPps}}
            <Checkbox
              @labelName='Cocher tout le traitement du plan de soin'
              @checked={{this.isWholeCarePlanSelected}}
              @onChange={{this.toggleCarePlanSelection}}
              @dataTestInput='whole-care-plan-checkbox'
            />
          {{/if}}
          <TreatmentFormModal @patient={{@patient}} as |displayFormTable|>
            <LinkButton
              {{on 'click' this.showTreatmentForm}}
              {{on 'click' displayFormTable}}
              data-test='link-to-care-plan-treatments'
            >
              <i class='pencil alternate icon'></i>
              {{#if this.hasPps}}
                Modifier le plan de soins
              {{else}}
                Ajouter un traitement au plan de soins
              {{/if}}
            </LinkButton>
          </TreatmentFormModal>
          <div class='treatment pl-[20px]'>
            <ul class='list-none p-0'>
              {{#each
                this.latestTreatmentPrescription.prescriptions
                as |prescription index|
              }}
                <li>
                  <UiCheckboxWithBlock
                    @checked={{this.hasMedication
                      this.changeset.medications
                      prescription
                    }}
                    @onChange={{fn this.togglePrescription prescription}}
                    data-test-input={{concat 'prescription-' index}}
                  >
                    <Details @prescription={{prescription}} />
                  </UiCheckboxWithBlock>
                </li>
              {{/each}}
            </ul>
          </div>
        </div>
        <div class='field'>
          <div class='fields'>
            <div class='fifteen wide field'>
              <Form.TextArea
                @labelName='Médicaments ALD ponctuels'
                @propertyName='otherMedicationsAld'
                data-test-input='other_medications_ald_textArea'
              />
            </div>
            <div class='field !mt-[1.82em]'>
              <div
                {{fuiDropdown}}
                class='ui icon top left pointing dropdown basic button'
                data-test-input='other_medications_ald_suggestions'
              >
                <i class='lightbulb icon outline'></i>
                <div class='menu'>
                  <div class='header'>
                    <i class='lightbulb icon outline'></i>
                    Suggestions de commentaires
                  </div>
                  {{#each this.suggestionComments as |suggestion|}}
                    <div
                      role='button'
                      class='item'
                      data-test-input={{concat
                        'other_medication_ald_'
                        suggestion
                      }}
                      {{on
                        'click'
                        (fn this.appendVaccine this.changeset suggestion)
                      }}
                    >
                      {{suggestion}}
                    </div>
                  {{/each}}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class='field'>
          <Form.TextArea
            @labelName='Médicament(s) hors ALD hors plan de soins'
            @propertyName='otherMedications'
          />
        </div>

        {{#if @patient.authorizesAppUsage}}
          <div class='grouped fields'>
            <div class='field'>
              <Checkbox
                @checked={{this.willSendMessage}}
                @onChange={{this.setWillSendMessage}}
                @dataTestInput='willSendChatMessage'
                @labelName='Envoyer un message au patient'
                @payloadKey='willSendMessage'
              />
            </div>

            <div class='field'>
              <LabeledTextArea
                @value={{this.chatMessage}}
                @onChange={{this.updateChatMessage}}
                data-test-send-chat-message='chatMessage'
                @propertyName='chatMessage'
                @rows={{4}}
              />
            </div>
          </div>

          <div class='semeia faded-text-color'>
            Un lien vers l'ordonnance accompagnera votre message.
          </div>
        {{/if}}

      </:content>
    </Provider>
  </template>
}
